import { useState, useCallback, useContext } from 'react';
import { AppContext } from '../context';
import { APIGetOrderAdminCandidates, APISetOrderAdministrator, parseApiError } from '../api';

/**
 * Смена администратора у чека — единая логика для всех страниц.
 *
 * Кандидатов и правила доступа считает сервер:
 *   GET  /orders/<id>/admin-candidates/   — кого можно выбрать;
 *   POST /orders/<id>/set-administrator/  — применить (меняет весь receipt_uid).
 *
 * onDone() — перезагрузка данных страницы после успеха.
 *
 * Использование:
 *   const { changeAdmin, adminModal } = useOrderAdmin({ onDone: loadAll });
 *   <CheckCard onChangeAdmin={changeAdmin} ... />
 *   <ChangeAdminModal {...adminModal} />
 */
export default function useOrderAdmin({ onDone } = {}) {
    const { token, showToast } = useContext(AppContext);
    const [modal, setModal] = useState({
        open: false, order: null, loading: false, saving: false, data: null,
    });

    const close = useCallback(
        () => setModal({ open: false, order: null, loading: false, saving: false, data: null }),
        [],
    );

    // Точка входа из карточки чека
    const changeAdmin = useCallback(async (order) => {
        if (!order) return;
        setModal({ open: true, order, loading: true, saving: false, data: null });

        const res = await APIGetOrderAdminCandidates(token, order.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось загрузить список админов'), 'danger');
            close();
            return;
        }
        setModal(p => (p.order?.id === order.id ? { ...p, loading: false, data: res } : p));
    }, [token, showToast, close]);

    // Выбор админа (или null — снять привязку)
    const onSelect = useCallback(async (adminId) => {
        setModal(p => ({ ...p, saving: true }));
        const orderId = modal.order?.id;
        if (!orderId) return;

        const res = await APISetOrderAdministrator(token, orderId, {
            administrator_id: adminId ?? null,
        });
        if (res?.error) {
            setModal(p => ({ ...p, saving: false }));
            showToast(parseApiError(res.error, 'Не удалось изменить админа'), 'danger');
            return;
        }
        close();
        showToast('Админ чека изменён ✓', 'success');
        onDone && onDone();
    }, [token, modal.order, showToast, close, onDone]);

    return {
        changeAdmin,
        adminModal: {
            open:    modal.open,
            loading: modal.loading,
            saving:  modal.saving,
            data:    modal.data,
            onSelect,
            onClose: close,
        },
    };
}